
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import Header from '../../home/components/Header';
import Footer from '../../home/components/Footer';

interface Product {
  id: number;
  name: string;
  category: string;
  categoryColor: string;
  description: string;
  image: string;
  price: string;
  priceLabel: string;
  rating: number;
  availability: string;
  availabilityColor: string;
  specs: {
    label: string;
    value: string;
  }[];
  buttonText: string;
}

export default function SecurityAppliancesPage() {
  const location = useLocation();
  const isNL = location.pathname.startsWith('/nl');
  const prefix = isNL ? '/nl' : '';

  const [compareList, setCompareList] = useState<number[]>([]);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const products: Product[] = [
    {
      id: 1,
      name: 'Cisco ASA 5516-X Next-Generation Firewall',
      category: isNL ? 'Next-Gen Firewall' : 'Next-Gen Firewall',
      categoryColor: 'blue',
      description: isNL
        ? 'Enterprise-grade next-generation firewall met geavanceerde dreigingsbescherming, applicatiezichtbaarheid en gecentraliseerd beheer.'
        : 'Enterprise-grade next-generation firewall with advanced threat protection, application visibility, and centralized management.',
      image:
        'https://public.readdy.ai/ai/img_res/c228a716f7ce889fae75e178c72368ef.jpg',
      price: '\u20AC3.035,99',
      priceLabel: isNL ? 'Per stuk' : 'Per unit',
      rating: 4.5,
      availability: isNL ? 'Op Voorraad' : 'In Stock',
      availabilityColor: 'green',
      specs: [
        { label: isNL ? 'Doorvoer:' : 'Throughput:', value: '1.2 Gbps' },
        { label: isNL ? 'VPN-tunnels:' : 'VPN Tunnels:', value: '750 IPSec' },
        { label: isNL ? 'Gelijktijdige sessies:' : 'Concurrent Sessions:', value: '400.000' },
        { label: isNL ? 'Vormfactor:' : 'Form Factor:', value: '1U Rackmount' },
      ],
      buttonText: isNL ? 'Offerte Aanvragen' : 'Add to Cart',
    },
    {
      id: 2,
      name: 'Fortinet FortiGate 200F UTM Firewall',
      category: isNL ? 'UTM-apparaat' : 'UTM Device',
      categoryColor: 'orange',
      description: isNL
        ? 'Unified threat management firewall met AI-gestuurde beveiliging, SD-WAN-mogelijkheden en uitgebreide dreigingsbescherming.'
        : 'Unified threat management firewall with AI-powered security, SD-WAN capabilities, and comprehensive threat protection.',
      image:
        'https://readdy.ai/api/search-image?query=Fortinet%20FortiGate%20200F%20UTM%20firewall%20appliance%20black%20chassis%20with%20red%20accents%20and%20LED%20indicators%20front%20view%20rackmount%20on%20clean%20white%20background%20professional%20product%20photography&width=400&height=300&seq=fortinet-fortigate-200f&orientation=landscape',
      price: '\u20AC4.507,99',
      priceLabel: isNL ? 'Per stuk' : 'Per unit',
      rating: 5,
      availability: isNL ? 'Op Voorraad' : 'In Stock',
      availabilityColor: 'green',
      specs: [
        { label: isNL ? 'Doorvoer:' : 'Throughput:', value: '20 Gbps' },
        { label: isNL ? 'Dreigingsbescherming:' : 'Threat Protection:', value: '9.5 Gbps' },
        { label: 'IPSec VPN:', value: '10 Gbps' },
        { label: isNL ? 'Interfaces:' : 'Interfaces:', value: '18x GE, 2x 10GE' },
      ],
      buttonText: isNL ? 'Offerte Aanvragen' : 'Add to Cart',
    },
    {
      id: 3,
      name: 'Palo Alto PA-3260 Next-Gen Firewall',
      category: isNL ? 'Next-Gen Firewall' : 'Next-Gen Firewall',
      categoryColor: 'red',
      description: isNL
        ? 'Krachtige next-generation firewall met machine learning-gestuurde dreigingspreventie en cloudgeleverde beveiliging.'
        : 'High-performance next-generation firewall with machine learning-powered threat prevention and cloud-delivered security.',
      image:
        'https://readdy.ai/api/search-image?query=Palo%20Alto%20PA-3260%20next%20generation%20firewall%20appliance%20silver%20and%20black%20chassis%20with%20orange%20accents%20front%20view%20rackmount%20on%20clean%20white%20background%20professional%20product%20photography&width=400&height=300&seq=paloalto-pa3260-firewall&orientation=landscape',
      price: '\u20AC17.479,99',
      priceLabel: isNL ? 'Adviesprijs' : 'MSRP',
      rating: 4.5,
      availability: isNL ? 'Op Voorraad' : 'In Stock',
      availabilityColor: 'green',
      specs: [
        { label: isNL ? 'App-ID Doorvoer:' : 'App-ID Throughput:', value: '63 Gbps' },
        { label: isNL ? 'Dreigingspreventie:' : 'Threat Prevention:', value: '25 Gbps' },
        { label: 'IPSec VPN:', value: '25 Gbps' },
        { label: isNL ? 'Max. sessies:' : 'Max Sessions:', value: '16M' },
      ],
      buttonText: isNL ? 'Offerte Aanvragen' : 'Add to Cart',
    },
    {
      id: 4,
      name: 'Check Point Quantum 6400 Security Gateway',
      category: isNL ? 'Beveiligingsgateway' : 'Security Gateway',
      categoryColor: 'green',
      description: isNL
        ? 'Enterprise beveiligingsgateway met ThreatCloud AI, zero-day bescherming en uitgebreide dreigingspreventie-mogelijkheden.'
        : 'Enterprise security gateway with ThreatCloud AI, zero-day protection, and comprehensive threat prevention capabilities.',
      image:
        'https://readdy.ai/api/search-image?query=Check%20Point%20Quantum%206400%20security%20gateway%20appliance%20black%20chassis%20with%20green%20LED%20indicators%20front%20view%20rackmount%20on%20clean%20white%20background%20professional%20product%20photography&width=400&height=300&seq=checkpoint-quantum-6400&orientation=landscape',
      price: '\u20AC11.499,99',
      priceLabel: isNL ? 'Per stuk' : 'Per unit',
      rating: 4,
      availability: isNL ? 'Voorbestelling' : 'Pre-Order',
      availabilityColor: 'yellow',
      specs: [
        { label: isNL ? 'Firewall-doorvoer:' : 'Firewall Throughput:', value: '40 Gbps' },
        { label: isNL ? 'Dreigingspreventie:' : 'Threat Prevention:', value: '12 Gbps' },
        { label: isNL ? 'VPN-doorvoer:' : 'VPN Throughput:', value: '20 Gbps' },
        { label: isNL ? 'Gelijktijdige verbindingen:' : 'Concurrent Connections:', value: '9M' },
      ],
      buttonText: isNL ? 'Voorbestellen' : 'Pre-Order',
    },
    {
      id: 5,
      name: 'SonicWall NSa 4700 Mid-Range Firewall',
      category: isNL ? 'Middensegment Firewall' : 'Mid-Range Firewall',
      categoryColor: 'purple',
      description: isNL
        ? 'Geavanceerde dreigingsbescherming firewall met real-time deep memory inspectie en cloudgebaseerde dreigingsinformatie.'
        : 'Advanced threat protection firewall with real-time deep memory inspection and cloud-based threat intelligence.',
      image:
        'https://readdy.ai/api/search-image?query=SonicWall%20NSa%204700%20firewall%20appliance%20black%20chassis%20with%20orange%20accents%20and%20LED%20indicators%20front%20view%20rackmount%20on%20clean%20white%20background%20professional%20product%20photography&width=400&height=300&seq=sonicwall-nsa4700-firewall&orientation=landscape',
      price: '\u20AC2.023,99',
      priceLabel: isNL ? 'Per stuk' : 'Per unit',
      rating: 4.5,
      availability: isNL ? 'Op Voorraad' : 'In Stock',
      availabilityColor: 'green',
      specs: [
        { label: isNL ? 'Firewall-doorvoer:' : 'Firewall Throughput:', value: '5.5 Gbps' },
        { label: isNL ? 'IPS-doorvoer:' : 'IPS Throughput:', value: '2.5 Gbps' },
        { label: isNL ? 'VPN-doorvoer:' : 'VPN Throughput:', value: '1.5 Gbps' },
        { label: isNL ? 'Max. verbindingen:' : 'Max Connections:', value: '500.000' },
      ],
      buttonText: isNL ? 'Offerte Aanvragen' : 'Add to Cart',
    },
    {
      id: 6,
      name: 'Juniper SRX4600 Services Gateway',
      category: isNL ? 'Services Gateway' : 'Services Gateway',
      categoryColor: 'teal',
      description: isNL
        ? 'Krachtige services gateway met geavanceerde dreigingspreventie, SD-WAN-mogelijkheden en gecentraliseerd beleidsbeheer.'
        : 'High-performance services gateway with advanced threat prevention, SD-WAN capabilities, and centralized policy management.',
      image:
        'https://readdy.ai/api/search-image?query=Juniper%20SRX4600%20services%20gateway%20appliance%20black%20chassis%20with%20green%20LED%20indicators%20front%20view%20rackmount%20on%20clean%20white%20background%20professional%20product%20photography&width=400&height=300&seq=juniper-srx4600-gateway&orientation=landscape',
      price: '\u20AC26.679,99',
      priceLabel: isNL ? 'Adviesprijs' : 'MSRP',
      rating: 4,
      availability: isNL ? 'Op Voorraad' : 'In Stock',
      availabilityColor: 'green',
      specs: [
        { label: isNL ? 'Firewall-doorvoer:' : 'Firewall Throughput:', value: '200 Gbps' },
        { label: isNL ? 'IPS-doorvoer:' : 'IPS Throughput:', value: '80 Gbps' },
        { label: isNL ? 'VPN-doorvoer:' : 'VPN Throughput:', value: '100 Gbps' },
        { label: isNL ? 'Vormfactor:' : 'Form Factor:', value: '2U Rackmount' },
      ],
      buttonText: isNL ? 'Offerte Aanvragen' : 'Add to Cart',
    },
  ];

  const toggleCompare = (productId: number) => {
    if (compareList.includes(productId)) {
      setCompareList(compareList.filter((id) => id !== productId));
    } else if (compareList.length < 4) {
      setCompareList([...compareList, productId]);
    }
  };

  const clearComparison = () => {
    setCompareList([]);
  };

  const renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <div key={`full-${i}`} className="w-4 h-4 flex items-center justify-center">
          <i className="ri-star-fill"></i>
        </div>,
      );
    }

    if (hasHalfStar) {
      stars.push(
        <div key="half" className="w-4 h-4 flex items-center justify-center">
          <i className="ri-star-half-fill"></i>
        </div>,
      );
    }

    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <div key={`empty-${i}`} className="w-4 h-4 flex items-center justify-center">
          <i className="ri-star-line"></i>
        </div>,
      );
    }

    return stars;
  };

  const getCategoryColorClasses = (color: string) => {
    const colorMap: { [key: string]: string } = {
      blue: 'text-blue-600 bg-blue-50',
      orange: 'text-orange-600 bg-orange-50',
      red: 'text-red-600 bg-red-50',
      green: 'text-green-600 bg-green-50',
      purple: 'text-purple-600 bg-purple-50',
      teal: 'text-teal-600 bg-teal-50',
    };
    return colorMap[color] || 'text-gray-600 bg-gray-50';
  };

  const getAvailabilityColorClasses = (color: string) => {
    const colorMap: { [key: string]: string } = {
      green: 'bg-green-100 text-green-800',
      yellow: 'bg-yellow-100 text-yellow-800',
      gray: 'bg-gray-100 text-gray-800',
    };
    return colorMap[color] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Breadcrumb */}
      <div className="bg-gray-50 py-3 border-b border-gray-200">
        <div className="container mx-auto px-4">
          <nav className="flex items-center space-x-2 text-sm">
            <a href={prefix || '/'} className="text-gray-600 hover:text-primary flex items-center">
              <div className="w-4 h-4 flex items-center justify-center mr-1">
                <i className="ri-home-line"></i>
              </div>
              Home
            </a>
            <div className="w-4 h-4 flex items-center justify-center text-gray-400">
              <i className="ri-arrow-right-s-line"></i>
            </div>
            <a href="#" className="text-gray-600 hover:text-primary">
              Hardware
            </a>
            <div className="w-4 h-4 flex items-center justify-center text-gray-400">
              <i className="ri-arrow-right-s-line"></i>
            </div>
            <a href="#" className="text-gray-600 hover:text-primary">
              {isNL ? 'Netwerkapparatuur' : 'Networking'}
            </a>
            <div className="w-4 h-4 flex items-center justify-center text-gray-400">
              <i className="ri-arrow-right-s-line"></i>
            </div>
            <span className="text-gray-900 font-medium">
              {isNL ? 'Beveiligingsapparatuur' : 'Security Appliances'}
            </span>
          </nav>
        </div>
      </div>

      {/* Header Section */}
      <div className="bg-white py-8">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-start">
            <div className="max-w-4xl">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {isNL ? 'Netwerkbeveiligingsapparatuur' : 'Network Security Appliances'}
              </h1>
              <p className="text-gray-600 mb-6">
                {isNL
                  ? 'Enterprise-grade netwerkbeveiligingshardware inclusief firewalls, VPN-apparaten, inbraakdetectiesystemen en unified threat management apparaten van toonaangevende beveiligingsfabrikanten.'
                  : 'Enterprise-grade network security hardware including firewalls, VPN appliances, intrusion detection systems, and unified threat management devices from leading security manufacturers.'}
              </p>

              {/* Category and Brand Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    {isNL ? 'Beveiligingscategorieën' : 'Security Categories'}
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-shield-line text-primary text-2xl"></i>
                      </div>
                      <span className="text-gray-700">Firewalls</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-lock-line text-green-600 text-2xl"></i>
                      </div>
                      <span className="text-gray-700">{isNL ? 'VPN-apparaten' : 'VPN Appliances'}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-eye-line text-blue-600 text-2xl"></i>
                      </div>
                      <span className="text-gray-700">{isNL ? 'IDS/IPS-systemen' : 'IDS/IPS Systems'}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-settings-3-line text-purple-600 text-2xl"></i>
                      </div>
                      <span className="text-gray-700">{isNL ? 'UTM-apparaten' : 'UTM Devices'}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    {isNL ? 'Toonaangevende Merken' : 'Leading Brands'}
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-building-line text-[#1BA0D7] text-2xl"></i>
                      </div>
                      <span className="text-gray-700">Cisco</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-shield-fill text-[#ED6C02] text-2xl"></i>
                      </div>
                      <span className="text-gray-700">Fortinet</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-fire-line text-[#FF6900] text-2xl"></i>
                      </div>
                      <span className="text-gray-700">Palo Alto</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-check-line text-[#00A651] text-2xl"></i>
                      </div>
                      <span className="text-gray-700">Check Point</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Filter Buttons */}
              <div className="flex flex-wrap gap-3 mb-6">
                <button className="px-4 py-2 bg-primary text-white rounded-button whitespace-nowrap text-sm font-medium hover:bg-primary/90">
                  {isNL ? 'Alle Beveiliging' : 'All Security'}
                </button>
                <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-button whitespace-nowrap text-sm font-medium hover:bg-gray-200">
                  Firewalls
                </button>
                <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-button whitespace-nowrap text-sm font-medium hover:bg-gray-200">
                  {isNL ? 'VPN-apparaten' : 'VPN Appliances'}
                </button>
                <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-button whitespace-nowrap text-sm font-medium hover:bg-gray-200">
                  IDS/IPS
                </button>
                <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-button whitespace-nowrap text-sm font-medium hover:bg-gray-200">
                  {isNL ? 'UTM-apparaten' : 'UTM Devices'}
                </button>
              </div>
            </div>

            {/* View Controls */}
            <div className="flex items-center space-x-4">
              <button className="flex items-center text-gray-600 hover:text-primary">
                <div className="w-5 h-5 flex items-center justify-center mr-2">
                  <i className="ri-scales-3-line"></i>
                </div>
                {isNL ? `Vergelijken (${compareList.length})` : `Compare (${compareList.length})`}
              </button>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">{isNL ? 'Weergave:' : 'View:'}</span>
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded ${
                    viewMode === 'grid' ? 'text-primary bg-blue-50' : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-grid-line"></i>
                  </div>
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded ${
                    viewMode === 'list' ? 'text-primary bg-blue-50' : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-list-check"></i>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 pb-16">
        <div className="flex gap-8">
          {/* Sidebar Filters */}
          <div className="w-80 flex-shrink-0">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sticky top-24">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold">{isNL ? 'Filters' : 'Filters'}</h3>
                <button className="text-primary text-sm hover:underline">
                  {isNL ? 'Alles Wissen' : 'Clear All'}
                </button>
              </div>

              {/* Product Type */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-3">{isNL ? 'Producttype' : 'Product Type'}</h4>
                <div className="space-y-2">
                  <label className="flex items-center cursor-pointer">
                    <input type="checkbox" className="filter-checkbox" />
                    <span className="ml-3 text-sm text-gray-700">
                      {isNL ? 'Next-Gen Firewalls' : 'Next-Gen Firewalls'}
                    </span>
                  </label>
                  {/* ...other filter checkboxes omitted for brevity */}
                </div>
              </div>

              {/* Additional filter sections omitted for brevity */}
            </div>
          </div>

          {/* Products Grid */}
          <div className="flex-1">
            {/* Sort and Filter Bar */}
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center space-x-4">
                <span className="text-gray-600">
                  {isNL
                    ? '6 van 89 beveiligingsapparaten weergegeven'
                    : 'Showing 6 of 89 security appliances'}
                </span>
              </div>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <label className="text-sm text-gray-600">{isNL ? 'Sorteren op:' : 'Sort by:'}</label>
                  <select className="border border-gray-300 rounded px-3 py-1 text-sm focus:ring-2 focus:ring-primary focus:border-primary pr-8 cursor-pointer">
                    <option value="relevance">{isNL ? 'Relevantie' : 'Relevance'}</option>
                    <option value="price-low">{isNL ? 'Prijs: Laag naar Hoog' : 'Price: Low to High'}</option>
                    <option value="price-high">{isNL ? 'Prijs: Hoog naar Laag' : 'Price: High to Low'}</option>
                    <option value="name">{isNL ? 'Naam A-Z' : 'Name A-Z'}</option>
                    <option value="popularity">{isNL ? 'Populariteit' : 'Popularity'}</option>
                    <option value="newest">{isNL ? 'Nieuwste Eerst' : 'Newest First'}</option>
                  </select>
                </div>
                <div className="flex items-center space-x-2">
                  <label className="text-sm text-gray-600">{isNL ? 'Toon:' : 'Show:'}</label>
                  <select className="border border-gray-300 rounded px-3 py-1 text-sm focus:ring-2 focus:ring-primary focus:border-primary pr-8 cursor-pointer">
                    <option value="12">12</option>
                    <option value="18" selected>
                      18
                    </option>
                    <option value="36">36</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Products Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((product) => (
                <div
                  key={product.id}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
                >
                  <div className="relative">
                    <div className="h-48 overflow-hidden bg-gray-100">
                      <img src={product.image} alt={product.name} className="w-full h-full object-contain" />
                    </div>
                    <div className="absolute top-3 left-3">
                      <span
                        className={`${getAvailabilityColorClasses(
                          product.availabilityColor,
                        )} text-xs font-medium px-2 py-1 rounded-full`}
                      >
                        {product.availability}
                      </span>
                    </div>
                    <div className="absolute top-3 right-3">
                      <input
                        type="checkbox"
                        className="w-5 h-5 cursor-pointer"
                        checked={compareList.includes(product.id)}
                        onChange={() => toggleCompare(product.id)}
                      />
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span
                        className={`text-xs font-medium ${getCategoryColorClasses(
                          product.categoryColor,
                        )} px-2 py-1 rounded-full`}
                      >
                        {product.category}
                      </span>
                      <div className="text-yellow-400 flex">{renderStars(product.rating)}</div>
                    </div>
                    <h3 className="text-lg font-semibold mb-1">{product.name}</h3>
                    <p className="text-gray-600 text-sm mb-3">{product.description}</p>
                    <div className="space-y-2 mb-4">
                      {product.specs.map((spec, index) => (
                        <div key={index} className="flex justify-between text-sm">
                          <span className="text-gray-600">{spec.label}</span>
                          <span className="font-medium">{spec.value}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="text-xs text-gray-500">{product.availability}</div>
                      </div>
                      <div className="flex space-x-2">
                        <button className="bg-gray-100 text-gray-600 px-3 py-2 rounded-button whitespace-nowrap text-sm hover:bg-gray-200 cursor-pointer">
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-eye-line"></i>
                          </div>
                        </button>
                        <a
                          href={`${prefix}/contact`}
                          className="bg-primary text-white px-3 py-2 rounded-button whitespace-nowrap text-sm hover:bg-primary/90 cursor-pointer"
                        >
                          {isNL ? 'Offerte Aanvragen' : 'Request a Quote'}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="flex justify-between items-center mt-12">
              <div className="text-gray-600">
                {isNL ? '1-18 van 89 resultaten weergegeven' : 'Showing 1-18 of 89 results'}
              </div>
              <div className="flex items-center space-x-2">
                <button
                  className="px-3 py-2 border border-gray-300 rounded text-gray-600 hover:bg-gray-50 disabled:opacity-50 cursor-pointer"
                  disabled
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-left-line"></i>
                  </div>
                </button>
                <button className="px-3 py-2 bg-primary text-white rounded cursor-pointer">1</button>
                <button className="px-3 py-2 border border-gray-300 rounded text-gray-600 hover:bg-gray-50 cursor-pointer">
                  2
                </button>
                <button className="px-3 py-2 border border-gray-300 rounded text-gray-600 hover:bg-gray-50 cursor-pointer">
                  3
                </button>
                <span className="px-2 text-gray-500">...</span>
                <button className="px-3 py-2 border border-gray-300 rounded text-gray-600 hover:bg-gray-50 cursor-pointer">
                  5
                </button>
                <button className="px-3 py-2 border border-gray-300 rounded text-gray-600 hover:bg-gray-50 cursor-pointer">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Comparison Bar */}
      {compareList.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-40">
          <div className="container mx-auto px-4 py-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-4">
                <span className="font-medium">
                  {isNL
                    ? `Beveiligingsapparatuur Vergelijken (${compareList.length}/4)`
                    : `Compare Security Appliances (${compareList.length}/4)`}
                </span>
                <div className="flex space-x-2">
                  {compareList.map((id) => {
                    const product = products.find((p) => p.id === id);
                    return (
                      <div key={id} className="bg-gray-100 px-3 py-1 rounded-full text-sm">
                        {product?.name.split(' ').slice(0, 2).join(' ')}
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <button onClick={clearComparison} className="text-gray-600 hover:text-gray-800 cursor-pointer">
                  {isNL ? 'Alles Wissen' : 'Clear All'}
                </button>
                <button className="bg-primary text-white px-6 py-2 rounded-button whitespace-nowrap font-medium hover:bg-primary/90 cursor-pointer">
                  {isNL ? 'Selectie Vergelijken' : 'Compare Selected'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
